import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link2, StickyNote, CheckSquare } from 'lucide-react';
import { TrajectPicker } from '@/components/crm/TrajectPicker';
import { traject, trajectPatch, type TrajectPatch, type TrajectKind } from '@/lib/traject';
import { useKoppelBericht, type EmailMessage } from '@/hooks/useEmailTriage';

type Soort = 'note' | 'todo';

interface Props {
  bericht: EmailMessage;
  /** Volledige, schoongemaakte tekst. Valt terug op de preview als die er niet is. */
  tekst?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Een bericht met de hand aan een dossier koppelen (T3.5, tweede helft).
 *
 * Het triage-scherm kon alleen suggesties goedkeuren of afwijzen, en suggesties
 * komen van de classifier. Zolang die niet draait — of als je hem bewust niet
 * gebruikt — was er geen enkele manier om post in een dossier te krijgen. Dit is
 * die manier.
 *
 * **Schrijft niet zelf naar het dossier.** Het maakt een suggestie en keurt die
 * meteen goed, zodat alles door `approve_email_suggestion` loopt: dezelfde
 * routering, dezelfde audittrail (`approved_by`, `result_record_id`), en later
 * dezelfde plek om te zien waar een notitie vandaan kwam. Een tweede schrijfpad
 * naast de RPC zou precies het soort stille afwijking opleveren waar F1 vol mee
 * zat.
 */
export function KoppelBericht({ bericht, tekst, open, onOpenChange }: Props) {
  const [soort, setSoort] = useState<Soort>('note');
  const [doel, setDoel] = useState<TrajectPatch>(trajectPatch('geen', null));
  const [titel, setTitel] = useState(bericht.subject ?? 'E-mail');
  const [tekstveld, setTekstveld] = useState(
    tekst?.trim() || bericht.body_preview || ''
  );
  const [termijn, setTermijn] = useState('');

  // De schoongemaakte tekst wordt pas opgehaald als deze dialoog opengaat, dus
  // bij de eerste render is `tekst` nog leeg en valt het veld terug op
  // `body_preview` — 255 tekens van Outlook, midden in een zin afgekapt. Een
  // `useState`-beginwaarde kijkt daarna niet meer, dus zonder dit effect zag je
  // permanent die halve mail in plaats van de hele.
  //
  // Alleen invullen zolang je zelf niet hebt getypt: wat jij aanpast is leidend.
  const zelfGetypt = useRef(false);
  useEffect(() => {
    if (!open) return;
    const schoon = tekst?.trim();
    if (schoon && !zelfGetypt.current) setTekstveld(schoon);
  }, [open, tekst]);

  // Opnieuw openen begint schoon: anders staat de vorige bewerking er nog.
  useEffect(() => {
    if (open) return;
    zelfGetypt.current = false;
  }, [open]);

  const koppel = useKoppelBericht();
  const gekozen = traject({
    project_id: doel.project_id,
    charging_location_id: doel.charging_location_id,
    charging_opportunity_id: doel.charging_opportunity_id,
  });
  const kanOpslaan = gekozen.kind !== 'geen' && titel.trim().length > 0;

  const opslaan = async () => {
    if (!kanOpslaan) return;
    await koppel.mutateAsync({
      emailId: bericht.id,
      soort,
      dossierSoort: gekozen.kind as Exclude<TrajectKind, 'geen'>,
      dossierId: gekozen.id!,
      titel: titel.trim(),
      tekst: tekstveld.trim(),
      termijn: termijn || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Koppelen aan een dossier
          </DialogTitle>
          <DialogDescription className="truncate">
            {bericht.from_name || bericht.from_email} &middot; {bericht.subject || '(geen onderwerp)'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs value={soort} onValueChange={(v) => setSoort(v as Soort)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="note">
                <StickyNote className="mr-1.5 h-4 w-4" />
                Notitie
              </TabsTrigger>
              <TabsTrigger value="todo">
                <CheckSquare className="mr-1.5 h-4 w-4" />
                Taak
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <TrajectPicker
            projectId={doel.project_id}
            chargingLocationId={doel.charging_location_id}
            chargingOpportunityId={doel.charging_opportunity_id}
            onChange={(patch) => setDoel(patch)}
          />

          <div className="space-y-2">
            <Label htmlFor="koppel-titel">{soort === 'todo' ? 'Wat moet er gebeuren' : 'Titel'}</Label>
            <Input
              id="koppel-titel"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              placeholder={soort === 'todo' ? 'Bijv. opzet indeling sturen' : 'Onderwerp van de notitie'}
            />
          </div>

          {soort === 'todo' && (
            <div className="space-y-2">
              <Label htmlFor="koppel-termijn">Uiterlijk (optioneel)</Label>
              <Input
                id="koppel-termijn"
                type="date"
                value={termijn}
                onChange={(e) => setTermijn(e.target.value)}
                className="w-48"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="koppel-tekst">
              {soort === 'todo' ? 'Toelichting' : 'Inhoud'}
            </Label>
            <Textarea
              id="koppel-tekst"
              value={tekstveld}
              onChange={(e) => { zelfGetypt.current = true; setTekstveld(e.target.value); }}
              rows={8}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Overgenomen uit de mail, handtekening en geciteerd antwoord eraf. Pas gerust aan —
              wat hier staat komt zo in het dossier.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuleren
          </Button>
          <Button onClick={opslaan} disabled={!kanOpslaan || koppel.isPending}>
            {koppel.isPending ? 'Bezig...' : soort === 'todo' ? 'Taak aanmaken' : 'Notitie opslaan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
